/**
 * Main Application Entry Point
 * Initializes React app, Capacitor, and deep linking
 * 
 * @author Varun Pandey
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import App from './App';
import { queryClient } from './api/queryClient';
import { DeepLinkService } from './services/deepLinkService';
import { runMigrations } from './storage/migrations';
import { installCapacitorFirebaseNetworkBypass } from './lib/capacitorFirebaseNetworkBypass';
import './index.css';

// Must run before any Firebase call so Firestore is not routed through CapacitorHttp.
installCapacitorFirebaseNetworkBypass();

// Mark real *physical* mobile devices so CSS safe-area rules don't trigger
// in desktop Chrome DevTools emulation (which spoofs a mobile UA but has no touch).
if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
  const ua = window.navigator.userAgent || '';
  const isMobileUA = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const hasTouch =
    'ontouchstart' in window ||
    (navigator as any).maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0;

  const isRealMobile = isMobileUA && hasTouch;

  if (isRealMobile) {
    document.documentElement.classList.add('is-real-mobile');
  } else {
    document.documentElement.classList.remove('is-real-mobile');
  }
}

// Initialize Capacitor plugins
if (Capacitor.isNativePlatform()) {
  // Set status bar style
  StatusBar.setStyle({ style: Style.Dark }).catch(() => {
    // Ignore errors on platforms that don't support status bar
  });

  // Configure keyboard
  Keyboard.setAccessoryBarVisible({ isVisible: true });
  Keyboard.setScroll({ isDisabled: false });
}

// Run storage migrations
runMigrations().catch((error) => {
  console.error('Storage migration error:', error);
});

// Initialize deep linking
DeepLinkService.initialize().catch((error) => {
  console.error('Deep link initialization error:', error);
});

console.log('App starting...');
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
